<template>
  <div class="supplier-profit-dashboard">
    <!-- Header with Gradient Area -->
    <div class="dashboard-header">
      <div class="header-content">
        <div class="header-left">
          <div class="icon-wrapper">
            <i class="fas fa-chart-line" />
          </div>
          <div>
            <h1>Analyse de Profit par Fournisseur</h1>
            <p class="subtitle">
              Visualisez le profit réel généré par chaque partenaire (basé sur les factures)
            </p>
          </div>
        </div>
        <router-link
          to="/expenses"
          class="btn-back"
        >
          <i class="fas fa-arrow-left" />
          Retour aux Dépenses
        </router-link>
      </div>
    </div>

    <!-- Period Filters -->
    <div class="filters-card">
      <div class="filter-header">
        <i class="fas fa-calendar-alt" />
        <span>Période d'analyse</span>
      </div>
      <div class="filter-controls">
        <div class="filter-group">
          <label>Date de début</label>
          <input
            v-model="filters.start_date"
            type="date"
            class="date-input"
            @change="loadStats"
          >
        </div>

        <div class="filter-group">
          <label>Date de fin</label>
          <input
            v-model="filters.end_date"
            type="date"
            class="date-input"
            @change="loadStats"
          >
        </div>

        <button
          class="btn-reset"
          @click="resetFilters"
        >
          <i class="fas fa-sync-alt" />
          Actualiser
        </button>
      </div>
    </div>

    <!-- KPI Metrics -->
    <div
      v-if="stats && stats.length > 0"
      class="kpi-grid"
    >
      <div class="kpi-card primary">
        <div class="kpi-icon">
          <i class="fas fa-hand-holding-usd" />
        </div>
        <div class="kpi-content">
          <div class="kpi-label">
            Profit Total
          </div>
          <div class="kpi-value">
            {{ formatAmount(totalProfit) }} DT
          </div>
          <div class="kpi-trend positive">
            <i class="fas fa-check-circle" /> Factures confirmées
          </div>
        </div>
      </div>

      <div class="kpi-card success">
        <div class="kpi-icon">
          <i class="fas fa-store" />
        </div>
        <div class="kpi-content">
          <div class="kpi-label">
            Fournisseurs
          </div>
          <div class="kpi-value">
            {{ stats.length }}
          </div>
          <div class="kpi-trend">
            Partenaires actifs
          </div>
        </div>
      </div>

      <div class="kpi-card warning">
        <div class="kpi-icon">
          <i class="fas fa-file-invoice" />
        </div>
        <div class="kpi-content">
          <div class="kpi-label">
            Total Factures
          </div>
          <div class="kpi-value">
            {{ totalInvoices }}
          </div>
          <div class="kpi-trend">
            Volumes de règlements
          </div>
        </div>
      </div>

      <div class="kpi-card info">
        <div class="kpi-icon">
          <i class="fas fa-percentage" />
        </div>
        <div class="kpi-content">
          <div class="kpi-label">
            Rentabilité Moy.
          </div>
          <div class="kpi-value">
            {{ formatAmount(averageProfit) }} DT
          </div>
          <div class="kpi-trend">
            Par fournisseur
          </div>
        </div>
      </div>
    </div>

    <!-- Charts and Analytics Visualization -->
    <div
      v-if="stats && stats.length > 0"
      class="visual-section"
    >
      <div class="charts-container-grid">
        <!-- Main Analysis Chart (Bar) -->
        <div class="chart-box large">
          <div class="chart-box-header">
            <h3>Classement Top Profit</h3>
            <p>Comparaison des 10 fournisseurs les plus rentables</p>
          </div>
          <div class="canvas-wrapper">
            <canvas ref="barChartCanvas" />
          </div>
        </div>

        <!-- Distribution Chart (Pie/Doughnut) -->
        <div class="chart-box">
          <div class="chart-box-header">
            <h3>Répartition du Profit</h3>
            <p>Part de marché par fournisseur</p>
          </div>
          <div class="canvas-wrapper donut">
            <canvas ref="pieChartCanvas" />
          </div>
        </div>
      </div>
    </div>

    <!-- Detailed Statistics Table -->
    <div
      v-if="stats && stats.length > 0"
      class="data-section"
    >
      <div class="table-card">
        <div class="table-card-header">
          <h3>Statistiques Détaillées</h3>
          <p>Analyse chiffrée par collaborateur</p>
        </div>
        <div class="table-responsive">
          <table class="modern-stats-table">
            <thead>
              <tr>
                <th class="text-center">
                  Rang
                </th>
                <th>Fournisseur</th>
                <th class="text-right">
                  Profit Généré
                </th>
                <th class="text-center">
                  Nb Factures
                </th>
                <th class="text-right">
                  Moyenne/Facture
                </th>
                <th>Performance</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, index) in stats"
                :key="item.supplier_id"
              >
                <td class="text-center">
                  <span
                    class="rank-indicator"
                    :class="index < 3 ? 'top-' + (index + 1) : ''"
                  >
                    {{ index + 1 }}
                  </span>
                </td>
                <td>
                  <div class="supplier-profile">
                    <div class="supplier-initials">
                      {{ getInitials(item.supplier_name) }}
                    </div>
                    <div class="supplier-details">
                      <span class="supplier-full-name">{{ item.supplier_name }}</span>
                      <span class="supplier-id-tag">#{{ item.supplier_id }}</span>
                    </div>
                  </div>
                </td>
                <td class="text-right font-weight-bold color-success">
                  {{ formatAmount(item.total_profit) }} DT
                </td>
                <td class="text-center">
                  <span class="invoice-badge">{{ item.expense_count }}</span>
                </td>
                <td class="text-right">
                  {{ formatAmount(item.total_profit / item.expense_count) }} DT
                </td>
                <td>
                  <div class="perf-bar-container">
                    <div
                      class="perf-bar-fill"
                      :style="{ width: (item.total_profit / stats[0].total_profit * 100) + '%' }"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Loading UI -->
    <div
      v-else-if="loading"
      class="state-display"
    >
      <div class="loader-circle" />
      <p>Analyse des données financières en cours...</p>
    </div>

    <!-- Empty/No Data UI -->
    <div
      v-else
      class="state-display empty"
    >
      <div class="empty-media">
        <i class="fas fa-search-dollar" />
      </div>
      <h3>Pas de données disponibles</h3>
      <p>Aucun profit enregistré pour ces critères. Vérifiez les dates sélectionnées.</p>
      <button
        class="btn-reset-alt"
        @click="resetFilters"
      >
        Voir tout
      </button>
    </div>
  </div>
</template>

<script>
/* eslint-disable func-names */
/* eslint-disable no-shadow */
import axios from 'axios'
import Chart from 'chart.js'

export default {
  name: 'SupplierProfitDashboard',
  data() {
    return {
      stats: [],
      loading: false,
      filters: {
        start_date: '',
        end_date: '',
      },
      charts: {
        bar: null,
        pie: null,
      },
    }
  },
  computed: {
    totalProfit() {
      let total = 0
      if (this.stats && this.stats.length > 0) {
        for (let i = 0; i < this.stats.length; i += 1) {
          total += parseFloat(this.stats[i].total_profit)
        }
      }
      return total
    },
    totalInvoices() {
      let count = 0
      if (this.stats && this.stats.length > 0) {
        for (let i = 0; i < this.stats.length; i += 1) {
          count += this.stats[i].expense_count
        }
      }
      return count
    },
    averageProfit() {
      return this.stats && this.stats.length > 0 ? this.totalProfit / this.stats.length : 0
    },
  },
  mounted() {
    this.loadStats()
  },
  beforeDestroy() {
    this.destroyCharts()
  },
  methods: {
    async loadStats() {
      this.loading = true
      try {
        const params = {}
        if (this.filters.start_date) params.start_date = this.filters.start_date
        if (this.filters.end_date) params.end_date = this.filters.end_date

        const response = await axios.get('/api/expenses/supplier-profit-stats/', { params })
        this.stats = response.data || []

        this.$nextTick(() => {
          // Small delay ensures DOM is fully painted for canvas refs
          setTimeout(this.renderAllCharts, 150)
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('SupplierProfitDashboard: API Error:', error)
        if (this.$toast) {
          this.$toast.error('Erreur lors du chargement des statistiques')
        }
      } finally {
        this.loading = false
      }
    },

    destroyCharts() {
      if (this.charts.bar) {
        this.charts.bar.destroy()
        this.charts.bar = null
      }
      if (this.charts.pie) {
        this.charts.pie.destroy()
        this.charts.pie = null
      }
    },

    renderAllCharts() {
      if (!this.stats || this.stats.length === 0) return
      this.destroyCharts()
      this.initBarChart()
      this.initPieChart()
    },

    initBarChart() {
      const canvas = this.$refs.barChartCanvas
      if (!canvas) {
        // eslint-disable-next-line no-console
        console.warn('SupplierProfitDashboard: Bar chart canvas ref not found')
        return
      }

      const top10 = this.stats.slice(0, 10)
      const ctx = canvas.getContext('2d')
      const self = this

      this.charts.bar = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: top10.map(s => s.supplier_name),
          datasets: [{
            label: 'Profit (DT)',
            data: top10.map(s => parseFloat(s.total_profit)),
            backgroundColor: 'rgba(16, 185, 129, 0.75)',
            borderColor: '#10b981',
            borderWidth: 2,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          tooltips: {
            backgroundColor: '#1f2937',
            titleFontSize: 14,
            bodyFontSize: 13,
            xPadding: 12,
            yPadding: 12,
            displayColors: false,
            callbacks: {
              label(tooltipItem) {
                return `Profit: ${self.formatAmount(tooltipItem.yLabel)} DT`
              },
            },
          },
          scales: {
            yAxes: [{
              ticks: {
                beginAtZero: true,
                callback(value) { return `${value} DT` },
                fontColor: '#6b7280',
              },
              gridLines: { color: 'rgba(0, 0, 0, 0.05)', zeroLineColor: '#e5e7eb' },
            }],
            xAxes: [{
              ticks: { fontColor: '#6b7280', fontSize: 11 },
              gridLines: { display: false },
            }],
          },
        },
      })
    },

    initPieChart() {
      const canvas = this.$refs.pieChartCanvas
      if (!canvas) {
        // eslint-disable-next-line no-console
        console.warn('SupplierProfitDashboard: Pie chart canvas ref not found')
        return
      }

      const top5 = this.stats.slice(0, 5)
      const ctx = canvas.getContext('2d')
      const self = this

      this.charts.pie = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: top5.map(s => s.supplier_name),
          datasets: [{
            data: top5.map(s => parseFloat(s.total_profit)),
            backgroundColor: [
              '#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6',
            ],
            borderWidth: 4,
            borderColor: '#ffffff',
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: {
            position: 'bottom',
            labels: { padding: 20, fontSize: 12, usePointStyle: true },
          },
          tooltips: {
            backgroundColor: '#1f2937',
            xPadding: 12,
            yPadding: 12,
            callbacks: {
              label(tooltipItem, data) {
                const value = data.datasets[0].data[tooltipItem.index]
                const total = self.totalProfit
                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0
                return `${data.labels[tooltipItem.index]}: ${self.formatAmount(value)} DT (${percentage}%)`
              },
            },
          },
        },
      })
    },

    resetFilters() {
      this.filters.start_date = ''
      this.filters.end_date = ''
      this.loadStats()
    },

    formatAmount(amount) {
      if (amount === undefined || amount === null) return '0.000'
      const val = parseFloat(amount)
      return Number.isNaN(val) ? '0.000' : val.toFixed(3)
    },

    getInitials(name) {
      if (!name) return '??'
      return name.split(' ').map(w => w[0]).join('').toUpperCase()
        .substring(0, 2)
    },
  },
}
</script>

<style scoped>
.supplier-profit-dashboard {
  padding: 30px;
  background-color: #f8fafc;
  min-height: 100vh;
}

/* Typography & Colors */
.color-success { color: #10b981; }

/* Dashboard Header */
.dashboard-header {
  background: linear-gradient(135deg, #10b981 0%, #065f46 100%);
  border-radius: 15px;
  padding: 35px;
  margin-bottom: 30px;
  box-shadow: 0 10px 25px rgba(16, 185, 129, 0.2);
  color: white;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 25px;
}

.icon-wrapper {
  width: 70px;
  height: 70px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
}

.dashboard-header h1 {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.5px;
  color: white;
}

.subtitle {
  margin: 8px 0 0 0;
  opacity: 0.9;
  font-size: 15px;
}

.btn-back {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  padding: 12px 22px;
  border-radius: 10px;
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
  transition: 0.2s;
  backdrop-filter: blur(5px);
}

.btn-back:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateX(-5px);
}

/* Filters */
.filters-card {
  background: white;
  border-radius: 15px;
  padding: 25px;
  margin-bottom: 25px;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.filter-header {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 700;
  color: #334155;
  font-size: 16px;
}

.filter-controls {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  align-items: flex-end;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-group label {
  font-size: 12px;
  text-transform: uppercase;
  font-weight: 600;
  color: #94a3b8;
}

.date-input {
  border: 2px solid #f1f5f9;
  border-radius: 10px;
  padding: 10px 15px;
  font-size: 14px;
  outline: none;
  transition: 0.2s;
}

.date-input:focus {
  border-color: #10b981;
  background: #f0fdf4;
}

.btn-reset {
  background: #334155;
  color: white;
  border: none;
  padding: 12px 25px;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: 0.2s;
}

.btn-reset:hover {
  background: #1e293b;
}

/* KPI Cards */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 25px;
  margin-bottom: 30px;
}

.kpi-card {
  background: white;
  border-radius: 15px;
  padding: 25px;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
  display: flex;
  align-items: center;
  gap: 20px;
  transition: 0.3s;
}

.kpi-card:hover { transform: translateY(-5px); }

.kpi-icon {
  width: 55px;
  height: 55px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}

.kpi-card.primary .kpi-icon { background: #dcfce7; color: #10b981; }
.kpi-card.success .kpi-icon { background: #dbeafe; color: #3b82f6; }
.kpi-card.warning .kpi-icon { background: #fef3c7; color: #f59e0b; }
.kpi-card.info .kpi-icon { background: #f3e8ff; color: #8b5cf6; }

.kpi-label { font-size: 13px; color: #64748b; margin-bottom: 5px; font-weight: 600; }
.kpi-value { font-size: 24px; font-weight: 800; color: #1e293b; }
.kpi-trend { font-size: 12px; color: #94a3b8; margin-top: 5px; display: flex; align-items: center; gap: 4px; }
.kpi-trend.positive { color: #10b981; }

/* Visual Section (Charts) */
.charts-container-grid {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 25px;
  margin-bottom: 30px;
}

.chart-box {
  background: white;
  border-radius: 15px;
  padding: 25px;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
}

.chart-box-header { margin-bottom: 20px; }
.chart-box-header h3 { margin: 0; font-size: 18px; color: #1e293b; font-weight: 700; }
.chart-box-header p { margin: 5px 0 0 0; font-size: 13px; color: #64748b; }

.canvas-wrapper { height: 350px; position: relative; }
.canvas-wrapper.donut { height: 300px; }

/* Data Table Section */
.table-card {
  background: white;
  border-radius: 15px;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
  overflow: hidden;
}

.table-card-header { padding: 25px; border-bottom: 1px solid #f1f5f9; }
.table-card-header h3 { margin: 0; font-size: 18px; font-weight: 700; }
.table-card-header p { margin: 5px 0 0 0; font-size: 13px; color: #64748b; }

.modern-stats-table { width: 100%; border-collapse: collapse; }
.modern-stats-table th {
  padding: 15px 25px;
  background: #f8fafc;
  font-size: 11px;
  text-transform: uppercase;
  color: #64748b;
  letter-spacing: 1px;
  font-weight: 700;
}

.modern-stats-table td { padding: 20px 25px; border-bottom: 1px solid #f1f5f9; font-size: 14px; }

/* Table Decorators */
.rank-indicator {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: #f1f5f9;
  font-weight: 700;
  color: #475569;
}

.rank-indicator.top-1 { background: #fef3c7; color: #92400e; }
.rank-indicator.top-2 { background: #f1f5f9; color: #64748b; }
.rank-indicator.top-3 { background: #ffedd5; color: #9a3412; }

.supplier-profile { display: flex; align-items: center; gap: 15px; }
.supplier-initials {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 13px;
}

.supplier-details { display: flex; flex-direction: column; }
.supplier-full-name { font-weight: 600; color: #1e293b; }
.supplier-id-tag { font-size: 11px; color: #94a3b8; }

.invoice-badge {
  background: #eff6ff;
  color: #3b82f6;
  padding: 4px 10px;
  border-radius: 6px;
  font-weight: 700;
  font-size: 12px;
}

.perf-bar-container { height: 8px; background: #f1f5f9; border-radius: 4px; overflow: hidden; width: 100px; }
.perf-bar-fill { height: 100%; background: #10b981; transition: width 1s ease-out; }

/* State Displays */
.state-display {
  background: white;
  border-radius: 15px;
  padding: 80px 40px;
  text-align: center;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
}

.loader-circle {
  width: 50px;
  height: 50px;
  border: 4px solid #f1f5f9;
  border-top-color: #10b981;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin { to { transform: rotate(360deg); } }

.empty-media { font-size: 60px; color: #cbd5e1; margin-bottom: 20px; }
.btn-reset-alt {
  margin-top: 20px;
  background: #10b981;
  color: white;
  border: none;
  padding: 10px 25px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
}

/* Responsive adjustments */
@media (max-width: 1200px) {
  .charts-container-grid { grid-template-columns: 1fr; }
}

@media (max-width: 768px) {
  .dashboard-header { padding: 25px; }
  .header-content { flex-direction: column; gap: 20px; text-align: center; }
  .header-left { flex-direction: column; gap: 15px; }
}
</style>
